import {
  DEFAULT_LEVEL,
  INSTRUMENT_PROFILES,
  THAALAMS,
  getProfile,
  getThaalam,
  noteToFrequency,
} from '@swarasense/shared';
import type { InstrumentId, PracticeLevel, RagaRecord } from '@swarasense/shared';
import type { AudioEngine } from '../audio/engine.js';
import type { Metronome } from '../audio/metronome.js';
import type { TanpuraSynth } from '../audio/tanpura.js';
import type { Store } from './store.js';
import { renderYourVoice } from './your-voice.js';

/** Everything the four popovers need to live-apply a change immediately, the
 * same audio objects `app.ts` owns for the whole page's lifetime. */
export interface PopoverDeps {
  store: Store;
  engine: AudioEngine;
  tanpura: TanpuraSynth;
  metronome: Metronome;
  /** Connected-Swarasense (W3/W5): the Sa popover is now the "Your voice" panel
   * (`your-voice.ts`), which needs the mic range-finder capture + the raga pool
   * for My-ragas. Supplied by `app.ts`. */
  captureSustainedHz: (onHeard?: (hz: number | null) => void) => Promise<number | null>;
  allRagas: () => RagaRecord[];
}

const PITCH_CLASSES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const OCTAVES = [2, 3, 4, 5];

/** Matches `AudioEngine.calibrateDrone`'s own default — the mic opens for one
 * continuous listen window rather than two back-to-back ones. */
const DETECT_DURATION_MS = 2500;

function opt(value: string, label: string, selected: boolean, disabled = false): string {
  return `<option value="${value}"${selected ? ' selected' : ''}${disabled ? ' disabled' : ''}>${label}</option>`;
}

/**
 * The DOM elements shared by all four popovers this module mounts: each
 * trigger button + its (initially empty) `role="dialog"` panel, and one
 * shared scrim (`--scrim` token) behind whichever popover is open. The
 * Settings/Sa TRIGGER buttons are built by `controls-row.ts`'s own template
 * (`mountControlsRow` returns those anchors, alongside the Tanpura toggle it
 * also builds — UI reimagine Task 2 moved that trigger here from the stage's
 * transport); the Thaalam TRIGGER button is built by `app.ts`'s stage
 * template, next to the transport toggle button it belongs to (metronome
 * controls regroup). All four PANELS, plus the shared scrim, are built by
 * `app.ts` itself in a top-level `.popover-layer` — a sibling of `.app`, not
 * nested inside `.controls-row`/`.stage` — so every panel is a true
 * `position: fixed` child of the viewport
 * (frontend-redesign-v2: previously each panel was `position: absolute`,
 * anchored to its trigger, which is exactly what let it clip past the stage
 * card's edge or cover the ribbon at different viewport heights). `app.ts`
 * merges the trigger anchors and its own panel/scrim elements into one
 * `PopoverAnchors` before calling `mountPopovers` once. This module only
 * fills the panels' contents and wires behaviour — none of that logic reads
 * DOM position, only element references, so moving the panels didn't change
 * anything here — mirroring the split already used for the ragam combobox
 * (the owning template builds the markup, a dedicated module owns the
 * interaction).
 */
export interface PopoverAnchors {
  settingsBtn: HTMLButtonElement;
  settingsPanel: HTMLElement;
  saBtn: HTMLButtonElement;
  saPanel: HTMLElement;
  saLabel: HTMLElement;
  /** The small "▾" affordance next to the transport Drone toggle — the toggle
   * itself still starts/stops the drone on click; this opens drone mode +
   * volume. */
  droneCaretBtn: HTMLButtonElement;
  dronePanel: HTMLElement;
  /** Same pattern as `droneCaretBtn`, for the Thaalam toggle. */
  thaalamCaretBtn: HTMLButtonElement;
  thaalamPanel: HTMLElement;
  scrim: HTMLElement;
}

/** Toggles the HTML `inert` attribute (removed from every accessibility/focus/
 * pointer path per spec — the same effect `aria-hidden` has on assistive tech,
 * plus it also can't be tabbed or clicked into) on every element in `els`. Used
 * to back the popovers' `aria-modal="true"` with real inertness: `aria-modal`
 * alone is only a hint some screen readers honour — it doesn't stop a sighted
 * keyboard user from tabbing into content behind the dialog. Attribute form
 * (not the `.inert` IDL property) so this works regardless of the configured
 * DOM lib version. */
function setInert(els: readonly Element[], on: boolean): void {
  for (const el of els) {
    if (on) el.setAttribute('inert', '');
    else el.removeAttribute('inert');
  }
}

/**
 * Everything OUTSIDE a given popover's own trigger that should go `inert`
 * while it's open. `keep` is the `.anchor` wrapping this popover's TRIGGER
 * (for Settings/Sa/Tanpura that's a direct child of the controls row; for
 * the transport's Thaalam options it's nested several levels deep inside
 * the stage) —
 * this walks from `keep` up to `appRoot`, and at every level along the way
 * collects that level's OTHER children (siblings of the node on the path), so
 * it inerts every other control in the bar, the other transport buttons, and
 * every other top-level section (stage/results/bar, whichever doesn't
 * contain `keep`) regardless of how deep `keep` is nested. The panel itself
 * lives in `.popover-layer`, OUTSIDE `.app` entirely (see `app.ts`), so this
 * walk — which only ever reaches as far up as `appRoot` (`.app`) — can never
 * inert it by construction, regardless of the explicit `scrim` exclusion
 * below (kept as defence in depth: it's what stops the scrim itself from
 * ever being inerted, since a click on it must still be able to close the
 * dialog). Falls back to inerting nothing (rather than throwing) if
 * `appRoot`/`keep` aren't found, so a structural change degrades gracefully
 * instead of breaking the popover entirely.
 */
function backgroundElsFor(
  appRoot: HTMLElement | null,
  keep: Element | null,
  scrim: HTMLElement,
): Element[] {
  if (!appRoot || !keep) return [];
  const els: Element[] = [];
  let node: Element = keep;
  while (node !== appRoot) {
    const parent: Element | null = node.parentElement;
    if (!parent) break;
    for (const child of parent.children) {
      if (child !== node && child !== scrim) els.push(child);
    }
    node = parent;
  }
  return els;
}

function focusablesIn(el: HTMLElement): HTMLElement[] {
  return Array.from(
    el.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((e) => e.getClientRects().length > 0);
}

/**
 * Wires the accessible-popover mechanics (WAI-ARIA dialog pattern) onto an
 * existing trigger + panel pair: `aria-expanded` on the trigger, a focus trap
 * inside the panel while open, Escape closes, a click/tap outside (including
 * on the shared scrim) closes, and focus returns to the trigger on close.
 * `registry` holds every popover this module has opened — used only to close
 * any OTHER open popover before opening this one, so Settings and Sa are
 * never shown at the same time.
 */
function attachPopover(
  trigger: HTMLButtonElement,
  panel: HTMLElement,
  scrim: HTMLElement,
  registry: Set<() => void>,
  signal: AbortSignal,
): { close(): void; readonly isOpen: boolean } {
  let open = false;
  let sessionAc: AbortController | null = null;
  // Computed lazily on first open (not at attach time) since `trigger` isn't
  // necessarily inserted under `.controls-row`/`.app` yet when `mountPopovers`
  // wires this up — by the time a user can actually click it, it is.
  let backgroundEls: Element[] | null = null;

  function doClose(): void {
    if (!open) return;
    open = false;
    panel.hidden = true;
    // Mutual exclusion (see `doOpen`) means at most one popover is ever open
    // at a time, so closing this one always means the scrim should go too.
    scrim.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
    if (backgroundEls) setInert(backgroundEls, false);
    sessionAc?.abort();
    sessionAc = null;
    registry.delete(doClose);
    trigger.focus();
  }

  function doOpen(): void {
    if (open) return;
    for (const closeOther of [...registry]) closeOther();
    open = true;
    registry.add(doClose);
    panel.hidden = false;
    scrim.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    // `aria-modal="true"` on the panel is only a hint some screen readers
    // honour; it doesn't stop a sighted keyboard (Tab) user from reaching
    // content behind the dialog. Actually inerting the rest of the page here
    // makes the modality real for everyone, not just the focus trap below.
    backgroundEls ??= backgroundElsFor(
      trigger.closest<HTMLElement>('.app'),
      trigger.closest('.anchor'),
      scrim,
    );
    setInert(backgroundEls, true);
    sessionAc = new AbortController();
    const { signal: sessionSignal } = sessionAc;

    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          doClose();
          return;
        }
        if (e.key !== 'Tab') return;
        const focusables = focusablesIn(panel);
        if (focusables.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusables[0]!;
        const last = focusables[focusables.length - 1]!;
        const active = document.activeElement;
        if (!panel.contains(active)) {
          e.preventDefault();
          first.focus();
          return;
        }
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      },
      { signal: sessionSignal },
    );
    // Capture phase so this fires before any in-panel handler, and so a click
    // on the scrim (which sits outside both `panel` and `trigger`) closes too.
    document.addEventListener(
      'pointerdown',
      (e) => {
        const target = e.target as Node;
        if (panel.contains(target) || trigger.contains(target)) return;
        doClose();
      },
      { signal: sessionSignal, capture: true },
    );

    const focusables = focusablesIn(panel);
    (focusables[0] ?? panel).focus();
  }

  trigger.setAttribute('aria-haspopup', 'dialog');
  trigger.setAttribute('aria-expanded', 'false');
  trigger.addEventListener('click', () => (open ? doClose() : doOpen()), { signal });
  signal.addEventListener('abort', () => doClose());

  return {
    close: doClose,
    get isOpen() {
      return open;
    },
  };
}

/**
 * Fills the top-bar ⚙ Settings popover with the fields that are genuinely
 * general — instrument/level/mic-sensitivity — none of which drive the
 * metronome/drone accompaniment. Thaalam/tempo/thaalam-volume and
 * drone-mode/drone-volume moved out to `renderThaalamPanel`/`renderDronePanel`
 * (metronome controls regroup) so each set of fields sits next to the
 * transport button it actually affects, instead of looking like a scoring
 * input buried in a general options panel. Wires each field to the store
 * PLUS the exact live-apply behavior the pre-Phase-4b setup screen's "More
 * settings" `<details>` had: the engine's input gain updates live regardless
 * of whether a take is in progress.
 */
function renderSettingsPanel(panel: HTMLElement, deps: PopoverDeps, signal: AbortSignal): void {
  const s = deps.store.get();
  panel.innerHTML = `
    <div class="popover-head">
      <h2 id="settings-popover-title">Settings</h2>
      <button class="popover-close" id="settings-close" type="button" aria-label="Close settings">&times;</button>
    </div>
    <div class="field">
      <label for="set-instrument">Instrument</label>
      <select id="set-instrument">${Object.values(INSTRUMENT_PROFILES)
        .map((p) => opt(p.id, p.label, p.id === s.instrument, !p.implemented))
        .join('')}</select>
    </div>
    <div class="field">
      <label for="set-level">Level</label>
      <select id="set-level">
        ${opt('beginner', 'Beginner', s.level === 'beginner')}
        ${opt('intermediate', 'Intermediate', s.level === 'intermediate')}
        ${opt('advanced', 'Advanced', s.level === 'advanced')}
      </select>
    </div>
    <div class="field">
      <label for="set-mic-gain">Mic sensitivity</label>
      <input id="set-mic-gain" type="range" min="1" max="5" step="0.1" value="${s.micGain}" />
    </div>`;

  const $ = <T extends HTMLElement>(id: string) => panel.querySelector<T>(`#${id}`)!;
  const bind = (id: string, ev: string, fn: () => void) => $(id).addEventListener(ev, fn, { signal });

  bind('set-instrument', 'change', () => {
    deps.store.set({ instrument: $<HTMLSelectElement>('set-instrument').value as InstrumentId });
  });
  bind('set-level', 'change', () => {
    deps.store.set({ level: $<HTMLSelectElement>('set-level').value as PracticeLevel });
  });
  bind('set-mic-gain', 'input', () => {
    const micGain = Number($<HTMLInputElement>('set-mic-gain').value);
    deps.store.set({ micGain });
    deps.engine.setInputGain(micGain);
  });
}

/**
 * Fills the Drone-options popover, opened from the small "▾" caret next to
 * the transport's Drone toggle (see app.ts) — the toggle itself still
 * starts/stops the drone on click; this panel only holds drone MODE and
 * VOLUME, exactly the two fields the old Settings popover had for it. Same
 * live-apply behavior as before: a running tanpura picks up a volume change
 * immediately. Drone mode itself has no "running" state to push into — the
 * transport's Drone toggle just reads `droneMode` from the store the next
 * time it starts (see app.ts).
 */
function renderDronePanel(panel: HTMLElement, deps: PopoverDeps, signal: AbortSignal): void {
  const s = deps.store.get();
  panel.innerHTML = `
    <div class="popover-head">
      <h2 id="drone-popover-title">Tanpura options</h2>
      <button class="popover-close" id="drone-close" type="button" aria-label="Close tanpura options">&times;</button>
    </div>
    <div class="field">
      <label for="set-drone-mode">Tanpura</label>
      <select id="set-drone-mode">
        ${opt('inapp', 'In-app (headphones)', s.droneMode === 'inapp')}
        ${opt('external', 'External', s.droneMode === 'external')}
      </select>
    </div>
    <div class="field">
      <label for="set-drone-vol">Tanpura volume</label>
      <input id="set-drone-vol" type="range" min="0" max="0.5" step="0.01" value="${s.droneVolume}" />
    </div>`;

  const $ = <T extends HTMLElement>(id: string) => panel.querySelector<T>(`#${id}`)!;
  const bind = (id: string, ev: string, fn: () => void) => $(id).addEventListener(ev, fn, { signal });

  bind('set-drone-mode', 'change', () => {
    deps.store.set({
      droneMode: $<HTMLSelectElement>('set-drone-mode').value as 'inapp' | 'external',
    });
  });
  bind('set-drone-vol', 'input', () => {
    const droneVolume = Number($<HTMLInputElement>('set-drone-vol').value);
    deps.store.set({ droneVolume });
    if (deps.tanpura.running) deps.tanpura.setVolume(droneVolume);
  });
}

/**
 * Fills the Thaalam-options popover, opened from the small "▾" caret next to
 * the transport's Thaalam toggle (see app.ts) — the toggle itself still
 * starts/stops the metronome on click; this panel holds thaalam name, tempo,
 * and thaalam volume, exactly the fields the old Settings popover had for
 * it. Same live-apply behavior as before: a running metronome picks up
 * thaalam/tempo/volume changes immediately.
 */
function renderThaalamPanel(panel: HTMLElement, deps: PopoverDeps, signal: AbortSignal): void {
  const s = deps.store.get();
  panel.innerHTML = `
    <div class="popover-head">
      <h2 id="thaalam-popover-title">Thaalam options</h2>
      <button class="popover-close" id="thaalam-close" type="button" aria-label="Close thaalam options">&times;</button>
    </div>
    <div class="field">
      <label for="set-thaalam">Thaalam</label>
      <select id="set-thaalam">${THAALAMS.map((t) => opt(t.name, t.name, t.name === s.thaalamName)).join('')}</select>
    </div>
    <div class="field">
      <label for="set-tempo">Tempo (bpm)</label>
      <input id="set-tempo" type="number" min="30" max="240" value="${s.tempo}" />
    </div>
    <div class="field">
      <label for="set-metro-vol">Thaalam volume</label>
      <input id="set-metro-vol" type="range" min="0" max="1" step="0.02" value="${s.thaalamVolume}" />
    </div>`;

  const $ = <T extends HTMLElement>(id: string) => panel.querySelector<T>(`#${id}`)!;
  const bind = (id: string, ev: string, fn: () => void) => $(id).addEventListener(ev, fn, { signal });

  bind('set-thaalam', 'change', () => {
    const thaalamName = $<HTMLSelectElement>('set-thaalam').value;
    deps.store.set({ thaalamName });
    if (deps.metronome.running) deps.metronome.setThaalam(getThaalam(thaalamName)!);
  });
  bind('set-tempo', 'change', () => {
    const tempo = Number($<HTMLInputElement>('set-tempo').value);
    deps.store.set({ tempo });
    if (deps.metronome.running) deps.metronome.setTempo(tempo);
  });
  bind('set-metro-vol', 'input', () => {
    const thaalamVolume = Number($<HTMLInputElement>('set-metro-vol').value);
    deps.store.set({ thaalamVolume });
    deps.metronome.setVolume(thaalamVolume);
  });
}

/**
 * Fills the Sa popover: pitch-class + octave selects and "Detect from drone".
 * Changing either select clears a stale `calibration` lock in the SAME
 * `store.set` call that updates the pitch class/octave (Task 1 review flagged
 * this was missing) — a lock measured against the old shruti must not keep
 * grading the new one. `app.ts`'s own store subscription (unchanged by this
 * task) is what actually restarts a running tanpura when pitchClass/octave
 * change; this module only ever writes to the store.
 */
function renderSaPanel(
  panel: HTMLElement,
  saLabel: HTMLElement,
  deps: PopoverDeps,
  signal: AbortSignal,
): void {
  // Connected-Swarasense (W3/W5): the Sa popover IS the "Your voice" panel now
  // — Sa dropdowns (always usable) + detect-from-tanpura + one-tap Find-my-Sa
  // + your level, goals, and My ragas. All of that lives in your-voice.ts; this
  // just hands it the panel and the deps. (This dissolved the separate "You"
  // tab.)
  renderYourVoice(
    panel,
    {
      store: deps.store,
      engine: deps.engine,
      captureSustainedHz: deps.captureSustainedHz,
      saLabel,
      allRagas: deps.allRagas,
    },
    signal,
  );
}

/**
 * Mounts all four popovers (Settings, Sa, Drone options, Thaalam options)
 * onto the anchors `app.ts` assembled (Settings/Sa TRIGGERS built by
 * `controls-row.ts`; the Drone caret TRIGGER also built by `controls-row.ts`
 * next to the Tanpura toggle it belongs to; the Thaalam caret TRIGGER built
 * by `app.ts`'s own stage template next to the transport toggle it belongs
 * to; all four PANELS + the shared scrim built by `app.ts`'s top-level
 * `.popover-layer`). Each panel's content
 * is rendered once, up front;
 * `showLockedStatus`/label refresh (Sa) and the field values (the other
 * three) reflect whatever the store held at mount time; every panel always
 * writes through `deps.store`, whose changes `app.ts` reacts to for
 * everything cross-cutting (rescoring on ragam change, restarting a running
 * drone on Sa change, etc.) — this module never needs to duplicate that
 * logic. All four share one `registry` (mutual exclusion — opening any one
 * closes whichever other of the four was open) and one scrim.
 */
export function mountPopovers(anchors: PopoverAnchors, deps: PopoverDeps, signal: AbortSignal): void {
  const registry = new Set<() => void>();

  renderSettingsPanel(anchors.settingsPanel, deps, signal);
  renderSaPanel(anchors.saPanel, anchors.saLabel, deps, signal);
  renderDronePanel(anchors.dronePanel, deps, signal);
  renderThaalamPanel(anchors.thaalamPanel, deps, signal);

  const settingsPopover = attachPopover(
    anchors.settingsBtn,
    anchors.settingsPanel,
    anchors.scrim,
    registry,
    signal,
  );
  const saPopover = attachPopover(anchors.saBtn, anchors.saPanel, anchors.scrim, registry, signal);
  const dronePopover = attachPopover(
    anchors.droneCaretBtn,
    anchors.dronePanel,
    anchors.scrim,
    registry,
    signal,
  );
  const thaalamPopover = attachPopover(
    anchors.thaalamCaretBtn,
    anchors.thaalamPanel,
    anchors.scrim,
    registry,
    signal,
  );

  anchors.settingsPanel
    .querySelector('#settings-close')
    ?.addEventListener('click', () => settingsPopover.close(), { signal });
  anchors.saPanel.querySelector('#sa-close')?.addEventListener('click', () => saPopover.close(), { signal });
  anchors.dronePanel
    .querySelector('#drone-close')
    ?.addEventListener('click', () => dronePopover.close(), { signal });
  anchors.thaalamPanel
    .querySelector('#thaalam-close')
    ?.addEventListener('click', () => thaalamPopover.close(), { signal });
}
